import { DecimalPipe, NgIf } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { MatButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { GeschaedigtesGeraet } from '@gsm/entities';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { setModelDisabled } from '../../helpers/disable-model.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { getGroupValue } from '../../helpers/get-group-value.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { GESCHAEDIGTES_GERAET_KOMPLEX_TEMPLATE_FACTORY } from './geschaedigte-geraete-komplex.config';
import { GeschaedigteGeraeteKomplexModel } from './geschaedigte-geraete-komplex.model';

@Component({
  selector:    'gsm-geschaedigte-geraete-komplex',
  templateUrl: 'geschaedigte-geraete-komplex.component.html',
  styleUrls:   ['geschaedigte-geraete-komplex.component.scss'],
  standalone:  true,
  imports:     [
    NgIf,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatMiniFabButton,
    FormsModule,
    ReactiveFormsModule,
    MatCard,
    MatButton,
    DecimalPipe,
    DynamicMaterialFormComponent
  ]
})
export class GeschaedigteGeraeteKomplexComponent extends CustomDynamicFormControlComponent<GeschaedigteGeraeteKomplexModel>
  implements OnInit {

  private formService = inject(CustomDynamicFormService);

  showForm = false;
  selectedItemIndex: number;

  entryFormModel: any;
  entryFormGroup: UntypedFormGroup;
  isSaving = false;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    if (!this.model.value) {
      this.model.value = [];
    }
    this.cd.detectChanges();
    this._emitChange();
  }

  protected onDisabledChange() {
    if (this.showForm && this.entryFormModel) {
      setModelDisabled(this.entryFormModel, this.model.disabled);
    }
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const isValid = !this.showForm && this.model.value?.length;
    return isValid ? null : {geschaedigteGeraeteKomplex: true};
  }

  setForm(model: GeschaedigtesGeraet) {
    this.showForm = true;

    const formConfig = GESCHAEDIGTES_GERAET_KOMPLEX_TEMPLATE_FACTORY(model);

    if (this.model.disabled) {
      setModelDisabled(formConfig, true);
    }

    this.entryFormModel = this.formService.fromJSON(formConfig);
    this.entryFormGroup = this.formService.createFormGroup(this.entryFormModel);

    // emit change, in order for scrolling in parent app to be triggered
    this._emitChange();
  }

  editItem(model: GeschaedigtesGeraet, index: number) {
    this.selectedItemIndex = index;
    this.setForm(this.model.value[index]);
  }

  removeItem(index: number) {
    // new copy of array is needed to trigger change detection
    const source = [...this.model.value];
    source.splice(index, 1);
    this.model.value = source;
    this._emitChange();
  }

  saveItem(event: Event) {
    // prevent form submission error on button click
    this.isSaving = true;
    event.preventDefault();

    const value = this._getEntryFormValue();

    const source = [...this.model.value];

    if (typeof this.selectedItemIndex === 'number') {
      source.splice(this.selectedItemIndex, 1, value);
    } else {
      source.push(value);
    }

    this.model.value = source;
    this.selectedItemIndex = null;
    this.cancelEdit();
    this.isSaving = false;

    this._emitChange();
  }

  cancelEdit() {
    this.showForm = false;
    this.selectedItemIndex = null;
    this._emitChange();
  }

  get isEntryFormValid(): boolean {
    if (this.entryFormGroup) {
      return this.entryFormModel && !formElementsInvalid(this.entryFormGroup, this.entryFormModel);
    }

    return false;
  }

  private _getEntryFormValue(): GeschaedigtesGeraet {
    const {
      id,
      status,
      details: {
                 art,
                 marke,
                 weitereInformationen,
                 fileList,
                 detaillierteInformationen: {
                                              baujahr,
                                              typenbezeichnung,
                                              seriennummer,
                                              anschaffungspreis,
                                              kostenReperaturNeuanschaffung
                                            } = {} as any
               } = {} as any
    } = getGroupValue(this.entryFormModel);
    return filterEmptyProps(
      {
        id,
        status,
        art,
        marke,
        weitereInformationen,
        fileList,
        baujahr:                       this._truncateYear(baujahr),
        typenbezeichnung,
        seriennummer,
        anschaffungspreis:             this._getCurrencyNumber(anschaffungspreis),
        kostenReperaturNeuanschaffung: this._getCurrencyNumber(kostenReperaturNeuanschaffung)
      },
      true
    );
  }

  private _getCurrencyNumber(val: number): number {
    return val && Math.round(val * 100 + Number.EPSILON) / 100;
  }

  private _truncateYear(val: number): string {
    return val && Math.trunc(val)
      .toString();
  }
}
